import { Settings } from './settings';
import { Signaling, NotifyListener, Notify } from './Signaling';
import { Peer } from './Peer';

export interface Connection {
    start(room: string): Promise<void>;
    stop(): Promise<void>;
    onNotify: ((notify: Notify) => void) | undefined;
}

abstract class ConnectionBase<Metadata> implements Connection {
    readonly settings: Settings<Metadata>;
    readonly role: string;

    private signaling: Signaling<Metadata>;

    protected peer?: Peer;

    constructor(settings: Settings<Metadata>, role: string) {
        this.settings = settings;
        this.role = role;

        this.signaling = new Signaling<Metadata>();
    }

    onNotify: ((notify: Notify) => void) | undefined;

    async start(room: string) {
        this.signaling.addNotifyListener(this.handleNotify);

        await this.signaling.start(this.settings, this.role, room, async (config, desc) => {
            const peer = new Peer(config);
            this.peer = peer;

            peer.addCandidateListener(candidate => {
                this.signaling.sendCandidate(candidate);
            });

            await peer.acceptOffer(desc);

            this.onOffer();

            const localSdp = await peer.createAnswer();
            this.signaling.sendAnswer(localSdp);
        });
    }

    async stop() {
        if (this.peer) {
            this.peer.clearStreamListener();
        }

        await this.signaling.stop();

        this.signaling.removeNotifyListener(this.handleNotify);
    }

    protected abstract onOffer(): void

    protected waitForStream(callback: (stream: MediaStream) => void) {
        const peer = this.peer;
        if (!peer) return;

        peer.addStreamListener(callback);
    }

    protected setStream(stream: MediaStream) {
        const peer = this.peer;
        if (!peer) return;

        peer.addStream(stream);
    }

    private handleNotify: NotifyListener = notify => {
        switch (notify.event_type) {
            case 'connection.created':
                console.log(notify);
                if (this.onNotify) this.onNotify(notify);
                break;

            case 'connection.updated':
                if (this.onNotify) this.onNotify(notify);
                break;

            case 'connection.destroyed':
                if (this.onNotify) this.onNotify(notify);
                break;
        }
    };
}

export class SubscriberConnection<Metadata> extends ConnectionBase<Metadata> {
    readonly connectListener: (stream: MediaStream) => void;

    constructor(settings: Settings<Metadata>, connectListener: (stream: MediaStream) => void) {
        super(settings, 'downstream');
        this.connectListener = connectListener;
    }

    onOffer() {
        this.waitForStream(stream => {
            this.connectListener(stream);
        });
    }
}

export class PublisherConnection<Metadata> extends ConnectionBase<Metadata> {
    readonly localStream: MediaStream;

    constructor(settings: Settings<Metadata>, localStream: MediaStream) {
        super(settings, 'upstream');

        this.localStream = localStream;
    }

    onOffer() {
        super.setStream(this.localStream);
    }
}
